import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { AppPage, AppCard } from '../../components/general';
import { OrgUnitCompensation } from '../../models/orgUnitCompensation';
import OrgUnitCompensationService from '../../services/orgUnitCompensationService';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { Messages } from 'primereact/messages';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { ToBooleanYesNo, ToDate } from '../../utils/string-helper';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCalendar, FormInputNumber, FormCheckbox } from '../../components/form';
import { filterOptions, filterTemplates } from '../../utils/data-table-lookups';

interface OrgUnitCompensationsProps {
  orgUnitId?: string;
  asPage?: boolean;
  onRefresh?: () => void;
}

interface OrgUnitCompensationWithDate extends OrgUnitCompensation {
  startDateObj?: Date;
}

interface OrgUnitCompensationFormData {
  originatorCompensationId?: number;
  startDate?: Date;
  loCompPercent?: number;
  minCompDollar?: number;
  maxCompDollar?: number;
  isBorrowerPaidDefault?: boolean;
  [key: string]: unknown;
}

const OrgUnitCompensations = ({ orgUnitId, asPage = true, onRefresh }: OrgUnitCompensationsProps = {}): JSX.Element => {
  const errors = useRef<Messages>(null);
  const toast = useRef<Toast>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [compensations, setCompensations] = useState<OrgUnitCompensationWithDate[]>([]);
  const [search, setSearch] = useState<string>('');
  const [editorDialogVisible, setEditorDialogVisible] = useState<boolean>(false);

  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<OrgUnitCompensation | null>(null);
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  const orgUnitCompensationService = OrgUnitCompensationService.getInstance();

  const { user, isLoading } = useAuth();

  const validationSchema = yup.object({
    startDate: yup.date().nullable().required('As of Date is required'),
    loCompPercent: yup.number().nullable().required('LO Comp Percent is required'),
  });

  const {
    control,
    formState: { errors: formErrors },
    reset,
    handleSubmit,
  } = useForm<OrgUnitCompensationFormData>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      startDate: undefined,
      loCompPercent: undefined,
      minCompDollar: undefined,
      maxCompDollar: undefined,
      isBorrowerPaidDefault: false,
    },
  });

  useEffect(() => {
    fetchCompensations();
  }, []);

  const fetchCompensations = (): void => {
    if (!orgUnitId) return;

    orgUnitCompensationService
      .getOrgUnitCompensations(orgUnitId)
      .then((results: OrgUnitCompensation[]) => {
        const processedResults = results.map((comp: OrgUnitCompensation) => ({
          ...comp,
          startDateObj: comp.startDate ? new Date(comp.startDate) : undefined,
        }));

        const sortedResults = [...processedResults].sort((a, b) => {
          if (!a.startDate) return 1;
          if (!b.startDate) return -1;
          return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        });

        setCompensations(sortedResults);
      })
      .catch((error: any) => {
        if (error.response && error.response.status === 404) {
          setCompensations([]);
          console.log('No compensation data found for org unit:', orgUnitId);
        } else {
          showError(`Failed to load compensation data: ${error.message}`);
        }
      });
  };

  const showError = (error: string) => {
    errors.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const showSaveConfirmation = (formData: OrgUnitCompensation) => {
    setConfirmationData(formData);
    setConfirmationOperation('save');
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);
    if (!confirmationData) return;

    if (confirmationOperation === 'save') {
      saveEditedCompensation(confirmationData);
    } else if (confirmationOperation === 'delete') {
      deleteCompensation(confirmationData);
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const onSubmitForm = (formData: OrgUnitCompensationFormData) => {
    const apiFormData: OrgUnitCompensation = {
      ...formData,
      startDate: formData.startDate ? formData.startDate.toISOString().split('T')[0] : undefined,
    };

    showSaveConfirmation(apiFormData);
  };

  const formatCompensationDate = (compensation: OrgUnitCompensation): OrgUnitCompensation => {
    const formattedComp = { ...compensation };

    if (formattedComp.startDate && typeof formattedComp.startDate === 'object') {
      formattedComp.startDate = (formattedComp.startDate as Date).toISOString();
    }

    return formattedComp;
  };

  const createCompensation = (compensation: OrgUnitCompensation) => {
    if (!orgUnitId) return;

    const formattedComp = formatCompensationDate(compensation);

    orgUnitCompensationService
      .create(orgUnitId, formattedComp)
      .then(() => {
        fetchCompensations();

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Compensation created successfully',
          life: 3000,
        });

        setEditorDialogVisible(false);

        if (onRefresh) {
          onRefresh();
        }
      })
      .catch((error: any) => {
        console.error('Error creating compensation:', error);
        if (error.response && error.response.status === 404) {
          showError('Compensation API is not available. Please contact your administrator.');
        } else {
          showError('Failed to create compensation. Please try again.');
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveEditedCompensation = (compensation: OrgUnitCompensation) => {
    setIsSaving(true);
    createCompensation(compensation);
  };

  const handleSave = () => {
    handleSubmit(onSubmitForm)();
  };

  const onDelete = (compensation: OrgUnitCompensation) => {
    setConfirmationData(compensation);
    setConfirmationOperation('delete');
    setConfirmDialogVisible(true);
  };

  const deleteCompensation = (compensation: OrgUnitCompensation) => {
    if (!compensation.originatorCompensationId || !orgUnitId) {
      showError('Cannot delete compensation: missing ID');
      return;
    }

    setIsDeleting(true);

    orgUnitCompensationService
      .delete(orgUnitId, compensation.originatorCompensationId)
      .then(() => {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Compensation record deleted successfully',
          life: 3000,
        });

        fetchCompensations();

        if (onRefresh) onRefresh();
      })
      .catch((error: any) => {
        console.error('Error deleting compensation:', error);
        if (error.response && error.response.status === 404) {
          showError('Compensation API is not available. Please contact your administrator.');
        } else {
          showError('Failed to delete compensation. Please try again.');
        }
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const actionTemplate = (compensation: OrgUnitCompensation) => {
    const isPastDate = (() => {
      if (!compensation.startDate) return false;

      const compDate = new Date(compensation.startDate);
      const today = new Date();

      compDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return compDate < today;
    })();

    if (isPastDate) {
      return (
        <i
          className="pi pi-lock text-color-secondary"
          style={{ fontSize: '0.875rem' }}
          title="Readonly due to being past"
        ></i>
      );
    }

    return (
      <div className="flex">
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => onDelete(compensation)}
          tooltip="Delete compensation"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  const tableDef: TableDefinition<OrgUnitCompensation> = {
    columns: [
      {
        field: 'startDateObj',
        header: 'Start Date',
        dataType: 'date',
        body: (compensation: OrgUnitCompensationWithDate) => ToDate(compensation.startDate),
        filterOptions: filterOptions.date,
        filterTemplate: filterTemplates.date,
      },
      {
        field: 'loCompPercent',
        header: 'LO Comp Percent',
        dataType: 'numeric',
        body: (compensation: OrgUnitCompensation) => {
          if (compensation.loCompPercent === undefined || compensation.loCompPercent === null) {
            return '';
          }
          return `${compensation.loCompPercent.toFixed(3)}%`;
        },
      },
      {
        field: 'minCompDollar',
        header: 'Min Comp Dollar',
        dataType: 'numeric',
        body: (compensation: OrgUnitCompensation) => {
          if (compensation.minCompDollar === undefined || compensation.minCompDollar === null) {
            return '';
          }
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(compensation.minCompDollar);
        },
      },
      {
        field: 'maxCompDollar',
        header: 'Max Comp Dollar',
        dataType: 'numeric',
        body: (compensation: OrgUnitCompensation) => {
          if (compensation.maxCompDollar === undefined || compensation.maxCompDollar === null) {
            return '';
          }
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(compensation.maxCompDollar);
        },
      },
      {
        field: 'isBorrowerPaidDefault',
        header: 'Borrower Paid Default',
        dataType: 'text',
        body: (compensation: OrgUnitCompensation) => ToBooleanYesNo(compensation.isBorrowerPaidDefault),
      },
      {
        header: 'Actions',
        body: actionTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  const renderContent = () => {
    if (isLoading || !user) {
      return (
        <div className="flex flex-column align-items-center justify-content-center gap-2">
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
        </div>
      );
    }

    return (
      <>
        <Toast ref={toast} />
        <Messages ref={errors} />
        <ConfirmDialog />

        {(isLoading || isDeleting) && <ProgressBar mode="indeterminate" style={{ height: '6px', marginBottom: '10px' }} />}

        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '350px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>
                Are you sure you want to delete this compensation record
                {confirmationData?.startDate ? ` from ${ToDate(confirmationData.startDate)}` : ''}?
              </span>
            )}
          </div>
        </Dialog>

        <DataTable
          header={
            <DataTableSearchCreateHeader
              onCreate={() => {
                reset({
                  startDate: undefined,
                  loCompPercent: undefined,
                  minCompDollar: undefined,
                  maxCompDollar: undefined,
                  isBorrowerPaidDefault: false,
                });
                setEditorDialogVisible(true);
              }}
              onSearchChange={setSearch}
            />
          }
          tableDef={tableDef}
          data={compensations}
          globalFilterValue={search}
          loading={isLoading}
        />

        {editorDialogVisible && (
          <Dialog
            header="Add Compensation"
            visible={editorDialogVisible}
            onHide={() => !isSaving && setEditorDialogVisible(false)}
            style={{ width: '35rem' }}
            closeOnEscape={!isSaving}
            closable={!isSaving}
            modal
            className="p-fluid"
            footer={
              <div className="flex justify-content-end">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => !isSaving && setEditorDialogVisible(false)}
                  disabled={isSaving}
                />
                <Button
                  label="Save"
                  icon={isSaving ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
                  onClick={handleSave}
                  disabled={isSaving}
                />
              </div>
            }
          >
            {isSaving && (
              <div
                className="absolute flex align-items-center justify-content-center"
                style={{
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  zIndex: 1000,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="5" />
              </div>
            )}
            <div className="grow p-fluid p-input-filled">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <FormCalendar
                    control={control}
                    errors={formErrors}
                    field="startDate"
                    label="As of Date"
                    minDate={new Date()}
                    showIcon
                  />
                </div>
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="loCompPercent"
                    label="LO Comp Percent"
                    mode="decimal"
                    suffix="%"
                    minFractionDigits={3}
                    maxFractionDigits={3}
                  />
                </div>
              </div>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="minCompDollar"
                    label="Min Comp Dollar"
                    mode="currency"
                    currency="USD"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <FormInputNumber
                    control={control}
                    errors={formErrors}
                    field="maxCompDollar"
                    label="Max Comp Dollar"
                    mode="currency"
                    currency="USD"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                  />
                </div>
              </div>
              <div className="grid">
                <div className="col-12">
                  <FormCheckbox
                    control={control}
                    errors={formErrors}
                    field="isBorrowerPaidDefault"
                    label="Borrower Paid Default"
                  />
                </div>
              </div>
            </div>
          </Dialog>
        )}
      </>
    );
  };

  if (asPage) {
    return (
      <AppPage title="Organization Unit Compensations" breadcrumbs="Admin - Organization Unit Compensations">
        <AppCard>{renderContent()}</AppCard>
      </AppPage>
    );
  }

  return <div className="p-3">{renderContent()}</div>;
};

export default OrgUnitCompensations;
